import ownAuthSession, { AUTH_API_URL } from './auth/ownAuthSession';

const NETAPI_URL = process.env.REACT_APP_NETAPI_URL || 'https://gtsnetapi.aquasoft.com.tr';

async function getAuthToken(): Promise<string> {
  const token = await ownAuthSession.getAccessToken();
  if (!token) {
    throw new Error('Kullanıcı oturum açmamış');
  }
  return token;
}

/**
 * @deprecated Node.js API (gtsapi.mt-teknoloji.com) kapatildi ve bu uclarin .NET karsiligi
 * henuz yok. Istek gondermek yerine hemen hata veriyoruz; aksi halde tarayici olu sunucuya
 * gidip CORS / ERR_FAILED uretiyor. Uclar .NET'e tasindikca cagrilar netApiRequest'e gecmeli.
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  throw new Error(
    `Bu ozellik su an kullanilamiyor: "${endpoint}" ucu kapatilan Node.js API'sine aitti ve .NET karsiligi henuz eklenmedi.`
  );
}

// SuperAdmin şirket yönetimi çekirdeği .NET API'sine (gts_dotnetapi) taşındı.
// Bu metotlar Node yerine NETAPI_URL + /api/superadmin uçlarına gider; Bearer own-JWT aynıdır.
// Diğer superadmin metotları (stats/tasks/hr/... vb.) şimdilik Node base'inde (apiRequest) kalır.
async function netApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${NETAPI_URL}/api/superadmin${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error((data as any)?.message || 'API isteği başarısız oldu');
    }

    return data as T;
  } catch (error) {
    console.error('NET API request error:', error);
    throw error;
  }
}

export interface Company {
  id: string;
  name: string;
  logo?: string;
  admin: {
    name: string;
    email: string;
    phone?: string;
  };
  maxPersonnel?: number;
  createdAt?: string;
  /** Şirket bir bayiye atanmışsa bayi Id/adı (backend GET companies yanıtından). */
  dealerId?: number | null;
  dealerName?: string | null;
  /** Demo bitiş tarihi (ISO); null = demo değil. demoExpired sunucuda hesaplanır. */
  demoUntil?: string | null;
  demoExpired?: boolean;
  subscriptionUntil?: string | null;
  subscriptionSource?: 'google_play' | 'app_store' | null;
  subscriptionAutoRenewing?: boolean | null;
  /** 0 = demo kullanılmış. */
  demoCarryOverDays?: number | null;
  accessState?: 'none' | 'active' | 'grace' | 'expired' | null;
  graceUntil?: string | null;
  /** Doluysa şirketin takibi bırakıldı: hatırlatma ve yenileme e-postası çıkmaz. */
  remindersMutedAt?: string | null;
  remindersMutedBy?: string | null;
  remindersMutedReason?: string | null;
  /**
   * Bayinin açtığı şirkete merkez henüz hiç modül izni vermedi (tavan boş liste).
   * "Modül bekliyor" rozeti için; bayisiz şirketlerde her zaman false.
   */
  awaitingModules?: boolean;
}

/** Bayi kaydı (GET /api/superadmin/dealers satırı). */
export interface DealerRow {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  isActive: boolean;
  createdAt?: string;
  companyCount: number;
  /** Bayinin kendi açabileceği şirket sayısı kotası. 0 = hiç açamaz. */
  maxCompanies: number;
  /** maxCompanies - companyCount (negatifse 0). */
  remainingQuota: number;
  users: Array<{ id: number; email: string; name: string; isActive: boolean }>;
}

/**
 * Çağıranın kendi şirket açma kotası (GET /api/superadmin/my-quota).
 * isDealer=false ise süper adminiz; kota alanları anlamsızdır (sınır yok).
 */
export interface MyQuotaInfo {
  isDealer: boolean;
  dealerName: string | null;
  /** Bayinin açabileceği toplam şirket sayısı. 0 = hiç açamaz. */
  maxCompanies: number;
  /** Bayiye bağlı mevcut şirket sayısı (merkezin elle atadıkları dahil). */
  companyCount: number;
  /** maxCompanies - companyCount (negatifse 0). */
  remainingQuota: number;
}

/** Yeni bayi oluşturma isteği (POST /api/superadmin/dealers). */
export interface CreateDealerRequest {
  name: string;
  email: string;
  password: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  /** Şirket açma kotası; verilmezse 0 (bayi hiç şirket açamaz). */
  maxCompanies?: number;
}

/** Şirket modül + demo bilgisi (GET /api/superadmin/companies/{id}/modules). */
export interface CompanyModulesInfo {
  /** null = tüm modüller açık (kısıtlama yok). */
  modules: string[] | null;
  /**
   * Bayiye izin verilen modül TAVANI. null = bayi kısıtlaması yok;
   * [] = merkez henüz izin vermedi. modules her zaman bu kümenin alt kümesidir.
   */
  dealerAllowedModules: string[] | null;
  /** Şirket bir bayiye atanmış mı (tavan bölümü yalnız bu durumda anlamlı). */
  isDealerManaged: boolean;
  /** İsteği yapan kullanıcı bayi mi — diyaloğun hangi modda açılacağını belirler. */
  scopeIsDealer: boolean;
  /** ISO tarih; null = demo değil (süresiz). */
  demoUntil: string | null;
  /** Sunucuda UTC ile hesaplanır. */
  demoExpired: boolean;
  subscriptionUntil?: string | null;
  subscriptionSource?: 'google_play' | 'app_store' | null;
  subscriptionAutoRenewing?: boolean | null;
  demoCarryOverDays?: number | null;
  accessState?: 'none' | 'active' | 'grace' | 'expired' | null;
  graceUntil?: string | null;
}

/** Yeni şirket + admin oluşturma isteği (POST /api/superadmin/companies). */
export interface CreateCompanyRequest {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
  maxPersonnel?: number;
}

/** Bir firmaya ait kullanıcı (impersonation seçim listesi için). */
export interface CompanyUser {
  id: number;
  firebaseUid?: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  branchesId?: string | null;
  employeeId?: string | null;
  lastLoginAt?: string | null;
  hasPassword: boolean;
}

export interface CompanyWithStats extends Company {
  stats: {
    personnelCount: number;
    branchCount: number;
    activeTasks: number;
    completedTasks: number;
    shifts: number;
    surveys: number;
  };
}

export interface ContactMessage {
  id: string;
  nameSurname: string;
  email: string;
  phone: string;
  createdAt: string;
  selectedPlan?: string;
  source?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

// ⚠️ GEÇİCİ — Yetki & Departman Taşıma tipleri
export interface PermissionRow {
  FirebaseUid: string;
  ScreenType: string;
  ScreenId: string | null;
  IsEnabled: boolean;
  SettingsJson: string | null;
}

export interface PermissionPreview {
  companyId: string;
  permissions: PermissionRow[];
  counts: {
    totalRows: number;
    personnelWithPermissions: number;
    byType: Record<string, number>;
  };
  uids: string[];
}

export interface DepartmentRow {
  Name: string;
  Description: string | null;
  Head: string | null;
  BranchesId: string | null;
  LegacyFirebaseKey: string;
  CreatedAt: string | null;
}

export interface DepartmentPreview {
  companyId: string;
  departments: DepartmentRow[];
  count: number;
}

export interface LegacyMigrationResult {
  byType: Record<string, { total: number; migrated: number; updated: number; errors: number }>;
  total: number;
  migrated: number;
  updated: number;
  errors: number;
}

export interface MigratePermissionsResult {
  companyId: string;
  permissions: {
    total: number;
    migrated: number;
    updated: number;
    orphan: number;
    errors: number;
    orphanUids: string[];
  };
  departments: {
    total: number;
    migrated: number;
    updated: number;
    errors: number;
  };
  legacy?: LegacyMigrationResult;
}

export interface MigrateAllResult {
  totals: {
    companies: number;
    succeeded: number;
    failed: number;
    permMigrated: number;
    permUpdated: number;
    permOrphan: number;
    deptMigrated: number;
    deptUpdated: number;
    legacyMigrated: number;
    legacyUpdated: number;
  };
  results: Array<{
    companyId: string;
    success: boolean;
    error?: string;
    status?: number;
    permissions?: MigratePermissionsResult['permissions'];
    departments?: MigratePermissionsResult['departments'];
    legacy?: LegacyMigrationResult;
  }>;
}

// ⚠️ GEÇİCİ — Görev backfill (RTDB → SQL) tipleri.
export interface TaskMigrationCounts {
  taskGroups: number;
  tasks: number;
  single: number;
  daily: number;
  weekly: number;
  monthly: number;
  completions: number;
  completionsCompleted: number;
  completionsMissed: number;
  completionsSkipped: number;
  comments: number;
  slots: number;
  teamGroups: number;
  teamMembers: number;
  teamProjects: number;
  teamTasks: number;
  yearlyPlans: number;
}

export interface TaskTypeStat {
  Migrated?: number;
  Updated?: number;
  Errors?: number;
  migrated?: number;
  updated?: number;
  errors?: number;
}

export interface TaskMigrationData {
  Groups?: TaskTypeStat;
  Tasks?: TaskTypeStat;
  Slots?: TaskTypeStat;
  Completions?: TaskTypeStat;
  Comments?: TaskTypeStat;
  TeamGroups?: TaskTypeStat;
  TeamMembers?: TaskTypeStat;
  TeamProjects?: TaskTypeStat;
  TeamTasks?: TaskTypeStat;
  TeamTaskHistory?: TaskTypeStat;
  YearlyPlans?: TaskTypeStat;
  OrphanPersonnelUids?: string[];
  [key: string]: any;
}

export interface MigrateTasksResult {
  companyId: string;
  counts: TaskMigrationCounts;
  result: TaskMigrationData;
}

export interface MigrateAllTasksResult {
  totals: {
    companies: number;
    succeeded: number;
    failed: number;
    tasksMigrated: number;
    tasksUpdated: number;
    completionsMigrated: number;
    completionsUpdated: number;
    orphan: number;
  };
  results: Array<{
    companyId: string;
    success: boolean;
    error?: string;
    status?: number;
    counts?: TaskMigrationCounts;
    result?: TaskMigrationData;
  }>;
}

interface CompaniesResponse {
  success: boolean;
  companies: Company[];
  count: number;
}

interface CompanyResponse {
  success: boolean;
  company: Company;
}

interface ContactsResponse {
  success: boolean;
  contacts: ContactMessage[];
  count: number;
}

export interface DeletionRequest {
  id: string;
  userId: string;
  email: string;
  requestDate: string;
  timestamp: number;
  status: 'pending' | 'completed';
  userInfo: {
    firstName: string;
    lastName: string;
    email: string;
    photoURL: string;
    companyId: string | null;
    role: string;
  };
}

interface DeletionRequestsResponse {
  success: boolean;
  requests: DeletionRequest[];
  count: number;
  message?: string;
}

interface HealthResponse {
  success: boolean;
  status: string;
  timestamp: string;
  uptime: number;
  firebase: {
    connected: boolean;
    totalCompanies: number;
    totalUsers: number;
  };
  server: {
    nodeVersion: string;
    platform: string;
    memory: {
      used: string;
      total: string;
    };
  };
}

interface LogsResponse {
  success: boolean;
  logs: Array<{
    timestamp: string;
    type: string;
    message: string;
    data: string | null;
  }>;
  count: number;
  totalLogs: number;
}

// ============================================
// AI CHAT (SuperAdmin yönetimi) TYPES
// ============================================

/** Şirket bazlı AI chat ayarı + bu ayki kullanım (SuperAdminAiChatController). */
export interface AiChatCompanyRow {
  companyId: string;
  companyName: string;
  enabled: boolean;
  /** AI Pro: ajan modu izni */
  agentEnabled: boolean;
  /** AI Pro: plan modu izni */
  planEnabled: boolean;
  monthlyTokenLimit: number;
  usedTokens: number;
  promptTokens: number;
  outputTokens: number;
  requestCount: number;
}

export interface AiChatAdminConversation {
  id: number;
  userId: string;
  userEmail: string | null;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  totalTokens: number;
}

export interface AiChatAdminMessage {
  id: number;
  role: string;
  content: string;
  promptTokens: number;
  outputTokens: number;
  totalTokens: number;
  model: string | null;
  createdAt: string;
}

/**
 * AI chat yönetim uçları /api/SuperAdminAiChat altında (netApiRequest'in /api/superadmin öneki uymaz).
 * NOT: gts_dotnetapi JSON'u camelCase serileştirir (PropertyNamingPolicy varsayılan);
 * bu yüzden yanıtlar camelCase okunur (success/data/companyId...).
 */
async function aiChatAdminRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const response = await fetch(`${NETAPI_URL}/api/SuperAdminAiChat${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  const success = (data as any)?.success ?? (data as any)?.Success;
  if (!response.ok || success === false) {
    throw new Error((data as any)?.message || (data as any)?.Message || 'API isteği başarısız oldu');
  }
  return data as T;
}

function asBoolOrNull(value: unknown): boolean | null {
  return value === true || value === false ? value : null;
}

function asIntOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function asAccessState(value: unknown): Company['accessState'] {
  return value === 'none' || value === 'active' || value === 'grace' || value === 'expired' ? value : null;
}

function asSubscriptionSource(value: unknown): Company['subscriptionSource'] {
  return value === 'google_play' || value === 'app_store' ? value : null;
}

/** Şirket listesi/detay gövdesindeki demo ve mobil abonelik alanları (camel veya Pascal). */
function normalizeCompanyAccess(raw: any): Pick<
  Company,
  | 'demoUntil'
  | 'demoExpired'
  | 'subscriptionUntil'
  | 'subscriptionSource'
  | 'subscriptionAutoRenewing'
  | 'demoCarryOverDays'
  | 'accessState'
  | 'graceUntil'
  | 'remindersMutedAt'
  | 'remindersMutedBy'
  | 'remindersMutedReason'
> {
  const pick = (camel: string, pascal: string) => raw?.[camel] ?? raw?.[pascal];
  const demoUntil = pick('demoUntil', 'DemoUntil');
  const graceUntil = pick('graceUntil', 'GraceUntil');
  const subscriptionUntil = pick('subscriptionUntil', 'SubscriptionUntil');
  return {
    demoUntil: typeof demoUntil === 'string' ? demoUntil : demoUntil ?? null,
    demoExpired: pick('demoExpired', 'DemoExpired') === true,
    subscriptionUntil: typeof subscriptionUntil === 'string' ? subscriptionUntil : subscriptionUntil ?? null,
    subscriptionSource: asSubscriptionSource(pick('subscriptionSource', 'SubscriptionSource')),
    subscriptionAutoRenewing: asBoolOrNull(pick('subscriptionAutoRenewing', 'SubscriptionAutoRenewing')),
    demoCarryOverDays: asIntOrNull(pick('demoCarryOverDays', 'DemoCarryOverDays')),
    accessState: asAccessState(pick('accessState', 'AccessState')),
    graceUntil: typeof graceUntil === 'string' ? graceUntil : graceUntil ?? null,
    remindersMutedAt: pick('remindersMutedAt', 'RemindersMutedAt') ?? null,
    remindersMutedBy: pick('remindersMutedBy', 'RemindersMutedBy') ?? null,
    remindersMutedReason: pick('remindersMutedReason', 'RemindersMutedReason') ?? null,
  };
}

class SuperAdminService {
  private static instance: SuperAdminService;

  private constructor() {}

  public static getInstance(): SuperAdminService {
    if (!SuperAdminService.instance) {
      SuperAdminService.instance = new SuperAdminService();
    }
    return SuperAdminService.instance;
  }

  async getAllCompanies(): Promise<Company[]> {
    const response = await netApiRequest<CompaniesResponse>('/companies', {
      method: 'GET',
    });
    if (!Array.isArray(response.companies)) return response.companies;
    return response.companies.map((raw) => ({ ...raw, ...normalizeCompanyAccess(raw) }));
  }

  async getCompany(companyId: string): Promise<CompanyWithStats> {
    const response = await netApiRequest<CompanyResponse>(`/companies/${companyId}`, {
      method: 'GET',
    });
    const company = response.company;
    if (!company) return company as CompanyWithStats;
    return { ...company, ...normalizeCompanyAccess(company) } as CompanyWithStats;
  }

  /**
   * Bir firmanın aktif kullanıcılarını Auth API'den getirir (impersonation seçim listesi).
   * Endpoint: GET {AUTH_API_URL}/api/auth/users/by-company/{companyId}
   * Yanıt camelCase (success/count/data) gelir.
   */
  async getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
    const token = await getAuthToken();
    const response = await fetch(
      `${AUTH_API_URL}/api/auth/users/by-company/${encodeURIComponent(companyId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();
    if (!response.ok || !(data.success ?? data.Success)) {
      throw new Error(data.message || data.Message || 'Kullanıcılar alınamadı');
    }

    const rows = (data.data ?? data.Data ?? []) as any[];
    return rows.map((u): CompanyUser => ({
      id: u.id ?? u.Id,
      firebaseUid: u.firebaseUid ?? u.FirebaseUid,
      email: u.email ?? u.Email ?? '',
      firstName: u.firstName ?? u.FirstName ?? '',
      lastName: u.lastName ?? u.LastName ?? '',
      role: u.role ?? u.Role ?? '',
      branchesId: u.branchesId ?? u.BranchesId ?? null,
      employeeId: u.employeeId ?? u.EmployeeId ?? null,
      lastLoginAt: u.lastLoginAt ?? u.LastLoginAt ?? null,
      hasPassword: u.hasPassword ?? u.HasPassword ?? false,
    }));
  }


  async updateMaxPersonnel(companyId: string, maxPersonnel: number): Promise<void> {
    await netApiRequest<ApiResponse<any>>(`/companies/${companyId}/max-personnel`, {
      method: 'PUT',
      body: JSON.stringify({ maxPersonnel }),
    });
  }

  async deleteCompany(companyId: string): Promise<{ log: string[] }> {
    const response = await netApiRequest<ApiResponse<any> & { log: string[] }>(
      `/companies/${companyId}/delete`,
      { method: 'POST' }
    );
    return { log: response.log || [] };
  }

  /**
   * Firebase'deki firma bilgilerini SQL Companies tablosuna senkronize eder.
   * QRMenu stub kayitlarini dolduran ya da hic SQL satiri olmayan firmalar icin.
   * Backend (CompanyManager.UpsertFromFirebaseAsync) idempotent calisir.
   */
  async syncCompanyToSql(companyId: string): Promise<{
    sqlId: number;
    name: string;
    lastSyncedAt: string;
  }> {
    const response = await apiRequest<
      ApiResponse<any> & { sqlId: number; name: string; lastSyncedAt: string }
    >(`/companies/${companyId}/sync-to-sql`, { method: 'POST' });
    return {
      sqlId: response.sqlId,
      name: response.name,
      lastSyncedAt: response.lastSyncedAt,
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // ⚠️ GEÇİCİ — Yetki & Departman Taşıma (RTDB → SQL).
  // Node RTDB'den okur; "Taşı" Node üzerinden custom token ile .NET'e yazar.
  // Adopsiyon (gts_hr_api P5) sonrası kaldırılacak.
  // ════════════════════════════════════════════════════════════════════════

  /** Şirketin RTDB yetkilerini normalize edilmiş satırlar olarak önizler (yazma YOK). */
  async getCompanyPermissions(companyId: string): Promise<PermissionPreview> {
    const response = await apiRequest<{ success: boolean; data: PermissionPreview }>(
      `/companies/${companyId}/permissions`,
      { method: 'GET' }
    );
    return response.data;
  }

  /** Şirketin RTDB departmanlarını önizler (yazma YOK). */
  async getCompanyDepartments(companyId: string): Promise<DepartmentPreview> {
    const response = await apiRequest<{ success: boolean; data: DepartmentPreview }>(
      `/companies/${companyId}/departments`,
      { method: 'GET' }
    );
    return response.data;
  }

  /** RTDB yetki + departmanları şirketin tenant DB'sine idempotent taşır (Taşı). */
  async migratePermissions(companyId: string): Promise<MigratePermissionsResult> {
    const response = await apiRequest<{ success: boolean } & MigratePermissionsResult>(
      `/companies/${companyId}/migrate-permissions`,
      { method: 'POST' }
    );
    return {
      companyId: response.companyId,
      permissions: response.permissions,
      departments: response.departments,
      legacy: response.legacy,
    };
  }

  /** TÜM şirketlerin yetki + departmanlarını tek seferde (firma seçmeden) taşır. */
  async migrateAllPermissions(): Promise<MigrateAllResult> {
    const response = await apiRequest<{ success: boolean } & MigrateAllResult>(
      `/companies/migrate-all-permissions`,
      { method: 'POST' }
    );
    return {
      totals: response.totals,
      results: response.results,
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // ⚠️ GEÇİCİ — Görev Backfill (RTDB → SQL). Node RTDB'den okur; "Taşı" Node
  // üzerinden custom token ile gts_task_api'ye yazar. Adopsiyon sonrası kaldırılacak.
  // ════════════════════════════════════════════════════════════════════════

  /** Şirketin RTDB görev ağacını önizler (yazma YOK) — dry-run sayım. */
  async getCompanyTasksPreview(companyId: string): Promise<{ companyId: string; counts: TaskMigrationCounts }> {
    const response = await apiRequest<{ success: boolean; data: { companyId: string; counts: TaskMigrationCounts } }>(
      `/companies/${companyId}/tasks-preview`,
      { method: 'GET' }
    );
    return response.data;
  }

  /** Şirketin RTDB görevlerini tenant SQL'e idempotent taşır (Taşı). */
  async migrateTasks(companyId: string): Promise<MigrateTasksResult> {
    const response = await apiRequest<{ success: boolean } & MigrateTasksResult>(
      `/companies/${companyId}/migrate-tasks`,
      { method: 'POST' }
    );
    return {
      companyId: response.companyId,
      counts: response.counts,
      result: response.result,
    };
  }

  /** TÜM şirketlerin görevlerini tek seferde (firma seçmeden) taşır. */
  async migrateAllTasks(): Promise<MigrateAllTasksResult> {
    const response = await apiRequest<{ success: boolean } & MigrateAllTasksResult>(
      `/companies/migrate-all-tasks`,
      { method: 'POST' }
    );
    return {
      totals: response.totals,
      results: response.results,
    };
  }

  async getAllContacts(): Promise<ContactMessage[]> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/ContactLeads/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'İletişim talepleri alınamadı');
      const contacts = Array.isArray(data.contacts) ? data.contacts : [];
      return contacts.map((c: any): ContactMessage => ({
        id: String(c.id),
        nameSurname: c.nameSurname,
        email: c.email,
        phone: c.phone,
        createdAt: c.createdAt,
        selectedPlan: c.selectedPlan ?? undefined,
        source: c.source ?? undefined,
      }));
    } catch (error) {
      console.error('İletişim talepleri alınırken hata:', error);
      throw error;
    }
  }

  async getDeletionRequests(): Promise<DeletionRequestsResponse> {
    return await apiRequest<DeletionRequestsResponse>('/deletion-requests', {
      method: 'GET',
    });
  }

  async markDeletionRequestCompleted(requestId: string): Promise<{ success: boolean; message: string }> {
    return await apiRequest<{ success: boolean; message: string }>('/deletion-request/mark-completed', {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    });
  }

  async deleteContact(contactId: string): Promise<void> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/ContactLeads/${contactId}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'İletişim talebi silinemedi');
      }
    } catch (error) {
      console.error('İletişim talebi silinirken hata:', error);
      throw error;
    }
  }

  async checkTasks(): Promise<{ log: string[]; totalCompanies: number; totalMissedTasks: number }> {
    const response = await apiRequest<any>('/check-tasks', {
      method: 'POST',
    });
    return {
      log: response.log || [],
      totalCompanies: response.totalCompanies || 0,
      totalMissedTasks: response.totalMissedTasks || 0,
    };
  }

  async getHealth(): Promise<HealthResponse> {
    return await apiRequest<HealthResponse>('/health', {
      method: 'GET',
    });
  }

  async getLogs(limit: number = 50, type?: string): Promise<LogsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      ...(type && { type }),
    });
    
    return await apiRequest<LogsResponse>(`/logs?${params.toString()}`, {
      method: 'GET',
    });
  }

  // Sözleşme uçları AdminScope: bayi de çağırabilir ama yalnız kendine atanmış
  // şirketler için (kapsam dışı şirkette 404). Bayi girdiğinde kayıt backend'de
  // DealerId ile damgalanır; yanıtta dealerId/dealerName döner.
  async createCompanyContract(companyId: string, startDate: string, endDate: string): Promise<any> {
    return await netApiRequest<any>(`/companies/${companyId}/contract`, {
      method: 'POST',
      body: JSON.stringify({ startDate, endDate }),
    });
  }

  async getCompanyContract(companyId: string): Promise<any> {
    return await netApiRequest<any>(`/companies/${companyId}/contract`, {
      method: 'GET',
    });
  }

  async getAllContracts(): Promise<any> {
    return await netApiRequest<any>('/contracts/all', {
      method: 'GET',
    });
  }

  // Login aktiviteleri .NET'e taşındı (gts_dotnetapi SuperAdmin). Sözleşme aynı: { success, count, data }.
  async getRecentLoginActivities(): Promise<any> {
    return netApiRequest<any>('/login-activities/recent', { method: 'GET' });
  }

  async getAllPersonnelActivities(): Promise<any> {
    return netApiRequest<any>('/login-activities/personnel', { method: 'GET' });
  }

  /**
   * Günlük trafik serisi (LoginActivityLogs → gün bazında).
   * Sözleşme: { success, days, data:[{ date, total, uniqueCompanies, uniqueUsers, web, mobile, other }] }.
   */
  async getLoginTrend(days = 30): Promise<any> {
    return netApiRequest<any>(`/login-activities/trend?days=${days}`, { method: 'GET' });
  }

  /**
   * Tek şirketin günlük giriş geçmişi.
   * Sözleşme: { success, companyId, days, total, data:[{ date, total, uniqueUsers, web, mobile, other }] }.
   */
  async getCompanyLoginTrend(companyId: string, days = 30): Promise<any> {
    return netApiRequest<any>(`/login-activities/company/${encodeURIComponent(companyId)}?days=${days}`, { method: 'GET' });
  }

  // ── İstek bazlı kullanım telemetrisi (gerçek kullanım + modül + online) ──

  /** Şu an online (son N sn'de istek atan) kullanıcılar. */
  async getUsageOnline(windowSeconds = 180): Promise<any> {
    return netApiRequest<any>(`/usage/online?windowSeconds=${windowSeconds}`, { method: 'GET' });
  }

  /** DAU/WAU/MAU, haftalık değişim, günlük seri ve saatlik dağılım. */
  async getUsageSummary(days = 30): Promise<any> {
    return netApiRequest<any>(`/usage/summary?days=${days}`, { method: 'GET' });
  }

  /** Modül özeti + şirket x modül matrisi. */
  async getUsageModules(days = 30, topCompanies = 30): Promise<any> {
    return netApiRequest<any>(`/usage/modules?days=${days}&topCompanies=${topCompanies}`, { method: 'GET' });
  }

  /** Tek şirketin modül kırılımı + günlük istek serisi. */
  async getCompanyUsageModules(companyId: string, days = 30): Promise<any> {
    return netApiRequest<any>(`/usage/company/${encodeURIComponent(companyId)}/modules?days=${days}`, { method: 'GET' });
  }

  /** Kullanımı belirgin düşen şirketler (churn erken uyarı). */
  async getUsageDeclining(): Promise<any> {
    return netApiRequest<any>('/usage/declining', { method: 'GET' });
  }

  /** Kişi bazında kim hangi modülü kullanıyor (iş modülü istekleri). */
  async getUsageUsers(days = 30, top = 500): Promise<any> {
    return netApiRequest<any>(`/usage/users?days=${days}&top=${top}`, { method: 'GET' });
  }

  async getAllUsers(): Promise<any> {
    return netApiRequest<any>('/users', { method: 'GET' });
  }

  /**
   * Yazarkasa listesi — sunucu taraflı arama + sayfalama.
   * Arama; şirket adı, marka, model, seri no, şehir, şube, kullanım alanı,
   * yetkili, VKN, cari unvan ve mobil hat alanlarında backend'de yapılır.
   */
  async getFiscalDevicesPaged(params: {
    page: number;
    pageSize: number;
    search?: string;
    companyId?: string;
    /** Şirket seçilmeden gönderilmez; backend de şirketsiz şube filtresini yok sayar. */
    branchId?: string;
    /**
     * Cihaz kaynağı. 'central' = yalnız merkezin verdikleri, 'dealer' = yalnız
     * bayilerin verdikleri, boş = hepsi. Bayi çağırdığında backend parametreyi
     * yok sayıp kapsamı zorla bayinin kendi cihazlarına sabitler.
     */
    source?: 'central' | 'dealer';
    /** source='dealer' ile tek bir bayiye daraltma (yalnız süper admin). */
    dealerId?: number;
  }): Promise<{ items: FiscalDeviceWithCompany[]; totalCount: number }> {
    try {
      const token = await getAuthToken();
      const qs = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
      });
      if (params.search?.trim()) qs.set('search', params.search.trim());
      if (params.companyId) qs.set('companyId', params.companyId);
      if (params.companyId && params.branchId) qs.set('branchId', params.branchId);
      if (params.source) qs.set('source', params.source);
      if (params.source === 'dealer' && params.dealerId) qs.set('dealerId', String(params.dealerId));
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/paged?${qs.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Cihazlar alınamadı');
      const raw = Array.isArray(data.data) ? data.data : [];
      const items: FiscalDeviceWithCompany[] = raw.map((d: any) => ({
        id: d.id ?? d.Id,
        companyId: d.companyId ?? d.CompanyId ?? '',
        companyName: d.companyName ?? d.CompanyName ?? null,
        dealerId: d.dealerId ?? d.DealerId ?? null,
        dealerName: d.dealerName ?? d.DealerName ?? null,
        vkn: d.vkn ?? d.VKN ?? null,
        authorizedPerson: d.authorizedPerson ?? d.AuthorizedPerson ?? null,
        phone: d.phone ?? d.Phone ?? null,
        email: d.email ?? d.Email ?? null,
        lineNumber: d.lineNumber ?? d.LineNumber ?? null,
        lineOwner: d.lineOwner ?? d.LineOwner ?? null,
        lineOperator: d.lineOperator ?? d.LineOperator ?? null,
        accountName: d.accountName ?? d.AccountName ?? null,
        city: d.city ?? d.City ?? null,
        branch: d.branch ?? d.Branch ?? null,
        branchId: d.branchId ?? d.BranchId ?? null,
        legacyBranch: d.legacyBranch ?? d.LegacyBranch ?? null,
        brand: d.brand ?? d.Brand ?? null,
        model: d.model ?? d.Model ?? null,
        serialNumber: d.serialNumber ?? d.SerialNumber ?? null,
        usageArea: d.usageArea ?? d.UsageArea ?? null,
        gmuFeeCollected: d.gmuFeeCollected ?? d.GmuFeeCollected ?? false,
        activationDate: d.activationDate ?? d.ActivationDate ?? null,
        expiryDate: d.expiryDate ?? d.ExpiryDate ?? null,
        invoiceStatus: d.invoiceStatus ?? d.InvoiceStatus ?? null,
        notes: d.notes ?? d.Notes ?? null,
        createdAt: d.createdAt ?? d.CreatedAt ?? '',
        updatedAt: d.updatedAt ?? d.UpdatedAt ?? '',
      }));
      return { items, totalCount: data.totalCount ?? 0 };
    } catch (error) {
      console.error('Yazarkasa cihazları (sayfalı) alınırken hata:', error);
      throw error;
    }
  }

  /**
   * Tüm şirketlerin platform entegrasyonları — sunucu taraflı arama + sayfalama.
   * Yemeksepeti remote code çakışma kontrolü için: aday kodu aratın,
   * sonuç yoksa kod güvenle kullanılabilir.
   */
  async getIntegrationsPaged(params: {
    page: number;
    pageSize: number;
    search?: string;
    platform?: string;
    companyId?: string;
    branchId?: string;
  }): Promise<{ items: PlatformIntegrationRow[]; totalCount: number }> {
    const qs = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
    });
    if (params.search?.trim()) qs.set('search', params.search.trim());
    if (params.platform) qs.set('platform', params.platform);
    if (params.companyId) qs.set('companyId', params.companyId);
    if (params.branchId) qs.set('branchId', params.branchId);
    const data = await netApiRequest<any>(`/integrations?${qs.toString()}`, { method: 'GET' });
    const raw = Array.isArray(data.data) ? data.data : (Array.isArray(data.Data) ? data.Data : []);
    const pickStr = (...vals: unknown[]): string | null => {
      for (const v of vals) {
        if (v === null || v === undefined) continue;
        const s = String(v).trim();
        if (s) return s;
      }
      return null;
    };
    // Backend anonim tip hem camelCase hem PascalCase dönebilir — tek şekle indir.
    // Yemeksepeti Chain Code = AppSecretKey (appSecretKey).
    const items: PlatformIntegrationRow[] = raw.map((r: any) => ({
      id: r.id ?? r.Id,
      companyId: r.companyId ?? r.CompanyId ?? '',
      companyName: r.companyName ?? r.CompanyName ?? '',
      branchId: pickStr(r.branchId, r.BranchId),
      branchName: pickStr(r.branchName, r.BranchName),
      platform: r.platform ?? r.Platform ?? '',
      category: r.category ?? r.Category ?? '',
      isEnabled: r.isEnabled ?? r.IsEnabled ?? false,
      storeName: pickStr(r.storeName, r.StoreName),
      restaurantId: pickStr(r.restaurantId, r.RestaurantId),
      sellerId: pickStr(r.sellerId, r.SellerId),
      apiKey: pickStr(r.apiKey, r.ApiKey),
      apiSecret: pickStr(r.apiSecret, r.ApiSecret),
      appSecretKey: pickStr(r.appSecretKey, r.AppSecretKey, r.chainCode, r.ChainCode),
      restaurantSecretKey: pickStr(r.restaurantSecretKey, r.RestaurantSecretKey),
      isConnected: r.isConnected ?? r.IsConnected ?? false,
      lastSyncAt: r.lastSyncAt ?? r.LastSyncAt ?? null,
      errorCount: r.errorCount ?? r.ErrorCount ?? 0,
      lastErrorMessage: pickStr(r.lastErrorMessage, r.LastErrorMessage),
      createdAt: r.createdAt ?? r.CreatedAt ?? '',
      updatedAt: r.updatedAt ?? r.UpdatedAt ?? '',
    }));
    return { items, totalCount: data.totalCount ?? data.TotalCount ?? 0 };
  }

  /**
   * Şirketin şube filtre seçenekleri (entegrasyon genel bakış modalı).
   * Backend şubeleri paylaşılan DB'den, yoksa şirketin özel tenant DB'sinden okur.
   */
  async getCompanyBranchOptions(companyId: string): Promise<BranchOption[]> {
    const data = await netApiRequest<any>(`/companies/${encodeURIComponent(companyId)}/branch-options`, { method: 'GET' });
    const raw = Array.isArray(data.data) ? data.data : [];
    return raw.map((b: any): BranchOption => ({
      branchId: b.branchId ?? b.BranchId ?? '',
      name: b.name ?? b.Name ?? '',
      isActive: b.isActive ?? b.IsActive ?? true,
      city: b.city ?? b.City ?? null,
      phone: b.phone ?? b.Phone ?? null,
      isFranchise: b.isFranchise ?? b.IsFranchise ?? false,
      firmName: b.firmName ?? b.FirmName ?? null,
      firmTaxNumber: b.firmTaxNumber ?? b.FirmTaxNumber ?? null,
      firmTaxOffice: b.firmTaxOffice ?? b.FirmTaxOffice ?? null,
      firmPhone: b.firmPhone ?? b.FirmPhone ?? null,
      email: b.email ?? b.Email ?? null,
      firmEmail: b.firmEmail ?? b.FirmEmail ?? null,
    }));
  }

  /**
   * Süresi dolmuş ya da yaklaşan takip kalemleri: yazarkasa GMÜ süreleri ve
   * şirket sözleşmeleri. Bayi kullanıcıda backend yalnızca kendi şirketlerinin
   * yazarkasalarını döner, sözleşmeler merkeze özeldir.
   */
  async getSuperAdminReminders(days = 30): Promise<SuperAdminReminder[]> {
    return (await this.getSuperAdminRemindersWithMeta(days)).rows;
  }

  /** Hatırlatmalar + susturulan kayıt sayısı (zil başlığındaki "Susturulanlar (n)" için). */
  async getSuperAdminRemindersWithMeta(days = 30): Promise<{ rows: SuperAdminReminder[]; mutedCount: number }> {
    const data = await netApiRequest<any>(`/reminders?days=${days}`, { method: 'GET' });
    const raw = Array.isArray(data.data) ? data.data : [];
    const rows = raw.map((r: any): SuperAdminReminder => ({
      kind: (r.kind ?? r.Kind ?? 'FISCAL_DEVICE') as SuperAdminReminderKind,
      key: r.key ?? r.Key ?? '',
      companyId: r.companyId ?? r.CompanyId ?? '',
      title: r.title ?? r.Title ?? '',
      subtitle: r.subtitle ?? r.Subtitle ?? null,
      detail: r.detail ?? r.Detail ?? null,
      expiryDate: r.expiryDate ?? r.ExpiryDate ?? null,
      daysRemaining: r.daysRemaining ?? r.DaysRemaining ?? 0,
      searchText: r.searchText ?? r.SearchText ?? null,
      targetTab: r.targetTab ?? r.TargetTab ?? 'fiscal-devices',
    }));
    return { rows, mutedCount: Number(data.mutedCount ?? data.MutedCount ?? 0) || 0 };
  }

  /** Uyarıyı ertele (snoozeDays) ya da bu dönem için kapat (snoozeDays = null). */
  async dismissReminder(key: string, snoozeDays: number | null, reason?: string | null): Promise<void> {
    await netApiRequest<any>('/reminders/dismiss', {
      method: 'POST',
      body: JSON.stringify({ key, snoozeDays, reason: reason || null }),
    });
  }

  /** Erteleme/kapatmayı geri al. */
  async restoreReminder(id: number): Promise<void> {
    await netApiRequest<any>('/reminders/restore', { method: 'POST', body: JSON.stringify({ id }) });
  }

  /** Susturulanlar: geçerli erteleme/kapatmalar + takibi bırakılmış şirketler. */
  async getMutedReminders(): Promise<MutedRemindersResult> {
    const data = await netApiRequest<any>('/reminders/muted', { method: 'GET' });
    const d = data?.data ?? {};
    return {
      dismissed: (d.dismissed ?? []).map((x: any): MutedReminder => ({
        id: x.id,
        key: x.key,
        kind: x.kind as SuperAdminReminderKind,
        companyId: x.companyId,
        title: x.title,
        subtitle: x.subtitle ?? null,
        expiryDate: x.expiryDate ?? null,
        daysRemaining: x.daysRemaining ?? 0,
        snoozedUntil: x.snoozedUntil ?? null,
        reason: x.reason ?? null,
        createdBy: x.createdBy ?? null,
        createdAt: x.createdAt,
      })),
      companies: (d.companies ?? []).map((x: any): MutedCompany => ({
        companyId: x.companyId,
        name: x.name,
        mutedAt: x.mutedAt ?? null,
        mutedBy: x.mutedBy ?? null,
        reason: x.reason ?? null,
      })),
    };
  }

  /** Şirketin takibini bırak (neden zorunlu). */
  async muteCompanyReminders(companyId: string, reason: string): Promise<void> {
    await netApiRequest<any>(`/companies/${encodeURIComponent(companyId)}/reminders-mute`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /** Şirketin takibini yeniden aç. */
  async unmuteCompanyReminders(companyId: string): Promise<void> {
    await netApiRequest<any>(`/companies/${encodeURIComponent(companyId)}/reminders-unmute`, { method: 'POST' });
  }

  async getAllFiscalDevices(): Promise<FiscalDevice[]> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Cihazlar alınamadı');
      return data.data || [];
    } catch (error) {
      console.error('Tüm yazarkasa cihazları alınırken hata:', error);
      throw error;
    }
  }

  /**
   * Bir şirketin yazarkasaları. `source='central'` yalnız merkezin verdiklerini (DealerId
   * NULL) döner — bayi cihazları tahsilat formuna girmez, Bayi Bildirimleri sekmesinden yönetilir.
   * Boş bırakılırsa (yönetim ekranı) kapsam backend'de scope'a göre belirlenir.
   */
  async getFiscalDevices(companyId: string, source?: 'central' | 'dealer'): Promise<FiscalDevice[]> {
    try {
      const token = await getAuthToken();
      const qs = source ? `?source=${source}` : '';
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/company/${companyId}${qs}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Cihazlar alınamadı');
      return data.data || [];
    } catch (error) {
      console.error('Yazarkasa cihazları alınırken hata:', error);
      throw error;
    }
  }

  async addFiscalDevice(device: FiscalDeviceCreateDto): Promise<FiscalDevice> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(device),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Cihaz eklenemedi');
      return data.data;
    } catch (error) {
      console.error('Yazarkasa cihazı eklenirken hata:', error);
      throw error;
    }
  }

  async updateFiscalDevice(id: number, device: FiscalDeviceUpdateDto): Promise<FiscalDevice> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/${id}/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(device),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Cihaz güncellenemedi');
      return data.data;
    } catch (error) {
      console.error('Yazarkasa cihazı güncellenirken hata:', error);
      throw error;
    }
  }

  async deleteFiscalDevice(id: number, companyId: string): Promise<void> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/${id}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ companyId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Cihaz silinemedi');
    } catch (error) {
      console.error('Yazarkasa cihazı silinirken hata:', error);
      throw error;
    }
  }

  async getCompaniesWithFiscalDevices(): Promise<string[]> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/companies-with-devices`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Veriler alınamadı');
      return data.data || [];
    } catch (error) {
      console.error('Yazarkasa şirketleri alınırken hata:', error);
      throw error;
    }
  }

  /**
   * Daha önce kullanılmış hat sahibi adları — form alanında öneri olarak sunulur.
   * Sabit bir liste tutulmaz; yeni yazılan bir firma adı kaydedildikten sonra
   * bir sonraki cihazda listeden seçilebilir. Öneri listesi kritik değil,
   * hata durumunda boş dönüp formu bloklamaz.
   */
  async getFiscalDeviceLineOwners(): Promise<string[]> {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${NETAPI_URL}/api/FiscalDevice/line-owners`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Hat sahipleri alınamadı');
      return Array.isArray(data.data) ? data.data : [];
    } catch (error) {
      console.error('Hat sahibi önerileri alınırken hata:', error);
      return [];
    }
  }

  // ============================================
  // MODÜL KONTROLÜ (şirket bazlı aç/kapa)
  // ============================================

  /**
   * Şirketin açık modül listesi + bayi tavanı + demo süresi.
   * modules=null → tüm modüller açık; demoUntil=null → demo değil (süresiz).
   * Modül id'leri src/config/moduleAccess.ts ile senkrondur.
   */
  async getCompanyModules(companyId: string): Promise<CompanyModulesInfo> {
    const data = await netApiRequest<any>(`/companies/${encodeURIComponent(companyId)}/modules`);
    const access = normalizeCompanyAccess(data);
    return {
      modules: data.modules ?? null,
      dealerAllowedModules: data.dealerAllowedModules ?? null,
      isDealerManaged: data.isDealerManaged === true,
      scopeIsDealer: data.scopeIsDealer === true,
      demoUntil: access.demoUntil ?? null,
      demoExpired: access.demoExpired === true,
      subscriptionUntil: access.subscriptionUntil ?? null,
      subscriptionSource: access.subscriptionSource ?? null,
      subscriptionAutoRenewing: access.subscriptionAutoRenewing ?? null,
      demoCarryOverDays: access.demoCarryOverDays ?? null,
      accessState: access.accessState ?? null,
      graceUntil: access.graceUntil ?? null,
    };
  }

  /**
   * Şirketin açık modül listesini ve demo süresini kaydeder.
   * modules=null → kısıtlamayı kaldırır (tümü açık); demoUntil=null → demoyu kaldırır.
   *
   * Bayi çağırdığında backend iki kısıt uygular: modules=null reddedilir ve
   * seçim bayi tavanının dışına çıkamaz (ikisi de 403). demoUntil yok sayılır.
   */
  async setCompanyModules(
    companyId: string,
    modules: string[] | null,
    demoUntil: string | null
  ): Promise<void> {
    await netApiRequest(`/companies/${encodeURIComponent(companyId)}/modules`, {
      method: 'POST',
      body: JSON.stringify({ modules, demoUntil }),
    });
  }

  /**
   * Bayiye izin verilen modül TAVANINI kaydeder (yalnız superadmin).
   * null → bayi kısıtlaması kaldırılır; [] → bayi hiçbir modül açamaz.
   * Tavandan çıkarılan modül şirketin açık modüllerinden de düşürülür.
   */
  async setDealerAllowedModules(
    companyId: string,
    modules: string[] | null
  ): Promise<{ dealerAllowedModules: string[] | null; modules: string[] | null }> {
    const data = await netApiRequest<any>(
      `/companies/${encodeURIComponent(companyId)}/dealer-allowed-modules`,
      {
        method: 'POST',
        body: JSON.stringify({ modules }),
      }
    );
    return {
      dealerAllowedModules: data.dealerAllowedModules ?? null,
      modules: data.modules ?? null,
    };
  }

  // ============================================
  // BAYİ SİSTEMİ (dealer yönetimi + şirket oluşturma)
  // ============================================

  /**
   * Şirket + admin kullanıcısı oluşturur (superadmin ve bayi kullanabilir;
   * bayi çağırırsa şirket backend'de otomatik o bayiye atanır).
   */
  async createCompany(request: CreateCompanyRequest): Promise<{ id: string; name: string; adminEmail: string }> {
    const data = await netApiRequest<any>('/companies', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return data.company;
  }

  /** Bayi listesi + şirket sayıları + bayi kullanıcıları (yalnız superadmin). */
  async getDealers(): Promise<DealerRow[]> {
    const data = await netApiRequest<any>('/dealers');
    return data.dealers ?? [];
  }

  /**
   * Çağıranın kendi şirket açma kotası. Bayi kullanıcısı kendi kotasını buradan
   * görür (bayi listesi ucu süper admine kapalı). Süper adminde isDealer=false
   * döner; kota sınırı yoktur.
   */
  async getMyQuota(): Promise<MyQuotaInfo> {
    const data = await netApiRequest<any>('/my-quota');
    return {
      isDealer: data.isDealer === true,
      dealerName: data.dealerName ?? null,
      maxCompanies: data.maxCompanies ?? 0,
      companyCount: data.companyCount ?? 0,
      remainingQuota: data.remainingQuota ?? 0,
    };
  }

  /** Yeni bayi + role='dealer' kullanıcısı oluşturur (yalnız superadmin). */
  async createDealer(request: CreateDealerRequest): Promise<{ id: number; name: string; email: string }> {
    const data = await netApiRequest<any>('/dealers', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return data.dealer;
  }

  /** Bayiyi aktif/pasif yapar; yeni durumu döner (yalnız superadmin). */
  async toggleDealer(dealerId: number): Promise<boolean> {
    const data = await netApiRequest<any>(`/dealers/${dealerId}/toggle`, { method: 'POST' });
    return data.isActive === true;
  }

  /**
   * Bayinin şirket açma kotasını günceller (yalnız superadmin).
   * Kota yalnız bayinin kendi oluşturmasını sınırlar; süper adminin elle
   * atadığı şirketler kotadan bağımsızdır, bu yüzden kota mevcut şirket
   * sayısının altına da çekilebilir.
   */
  async updateDealerMaxCompanies(
    dealerId: number,
    maxCompanies: number
  ): Promise<{ maxCompanies: number; companyCount: number; remainingQuota: number }> {
    const data = await netApiRequest<any>(`/dealers/${dealerId}/max-companies`, {
      method: 'POST',
      body: JSON.stringify({ maxCompanies }),
    });
    return {
      maxCompanies: data.maxCompanies ?? 0,
      companyCount: data.companyCount ?? 0,
      remainingQuota: data.remainingQuota ?? 0,
    };
  }

  /** Şirketi bayiye atar (şirket başka bayideyse taşınır). */
  async assignCompanyToDealer(dealerId: number, companyId: string): Promise<void> {
    await netApiRequest(`/dealers/${dealerId}/assign-company`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  }

  /** Şirketin bayi atamasını kaldırır (merkez yönetimine döner). */
  async unassignCompanyFromDealer(dealerId: number, companyId: string): Promise<void> {
    await netApiRequest(`/dealers/${dealerId}/unassign-company`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  }

  // ============================================
  // AI CHAT ASİSTANI (şirket bazlı yönetim)
  // ============================================

  /** Şirket listesi + AI bayrağı + limit + bu ayki kullanım. */
  async getAiChatCompanies(): Promise<AiChatCompanyRow[]> {
    const data = await aiChatAdminRequest<any>('/companies');
    return (data.data ?? data.Data ?? []).map((c: any) => ({
      companyId: c.companyId ?? c.CompanyId ?? '',
      companyName: c.companyName ?? c.CompanyName ?? '',
      enabled: (c.enabled ?? c.Enabled) === true,
      agentEnabled: (c.agentEnabled ?? c.AgentEnabled) === true,
      planEnabled: (c.planEnabled ?? c.PlanEnabled) === true,
      monthlyTokenLimit: Number(c.monthlyTokenLimit ?? c.MonthlyTokenLimit) || 0,
      usedTokens: Number(c.usedTokens ?? c.UsedTokens) || 0,
      promptTokens: Number(c.promptTokens ?? c.PromptTokens) || 0,
      outputTokens: Number(c.outputTokens ?? c.OutputTokens) || 0,
      requestCount: Number(c.requestCount ?? c.RequestCount) || 0,
    }));
  }

  /** Şirketin AI chat bayrağını aç/kapat. */
  async toggleAiChat(companyId: string, enabled: boolean): Promise<void> {
    await aiChatAdminRequest('/toggle', {
      method: 'POST',
      body: JSON.stringify({ companyId, enabled }),
    });
  }

  /** Şirketin AI Pro (ajan modu) bayrağını aç/kapat. */
  async toggleAiChatAgent(companyId: string, enabled: boolean): Promise<void> {
    await aiChatAdminRequest('/toggle-agent', {
      method: 'POST',
      body: JSON.stringify({ companyId, enabled }),
    });
  }

  /** Şirketin Plan Modu bayrağını aç/kapat. */
  async toggleAiChatPlan(companyId: string, enabled: boolean): Promise<void> {
    await aiChatAdminRequest('/toggle-plan', {
      method: 'POST',
      body: JSON.stringify({ companyId, enabled }),
    });
  }

  /** Şirketin aylık AI token limitini güncelle. */
  async setAiChatLimit(companyId: string, monthlyTokenLimit: number): Promise<void> {
    await aiChatAdminRequest('/limit', {
      method: 'PUT',
      body: JSON.stringify({ companyId, monthlyTokenLimit }),
    });
  }

  /** Şirketin AI konuşma listesi (denetim). */
  async getAiChatConversations(companyId: string): Promise<AiChatAdminConversation[]> {
    const data = await aiChatAdminRequest<any>(`/conversations?companyId=${encodeURIComponent(companyId)}`);
    return (data.data ?? data.Data ?? []).map((c: any) => ({
      id: c.id ?? c.Id ?? 0,
      userId: c.userId ?? c.UserId ?? '',
      userEmail: c.userEmail ?? c.UserEmail ?? null,
      title: c.title ?? c.Title ?? '',
      createdAt: c.createdAt ?? c.CreatedAt ?? '',
      updatedAt: c.updatedAt ?? c.UpdatedAt ?? '',
      messageCount: c.messageCount ?? c.MessageCount ?? 0,
      totalTokens: Number(c.totalTokens ?? c.TotalTokens) || 0,
    }));
  }

  /** Konuşmanın mesaj dökümü (denetim). */
  async getAiChatConversationMessages(conversationId: number): Promise<AiChatAdminMessage[]> {
    const data = await aiChatAdminRequest<any>(`/conversations/${conversationId}/messages`);
    return (data.data ?? data.Data ?? []).map((m: any) => ({
      id: m.id ?? m.Id ?? 0,
      role: m.role ?? m.Role ?? 'user',
      content: m.content ?? m.Content ?? '',
      promptTokens: m.promptTokens ?? m.PromptTokens ?? 0,
      outputTokens: m.outputTokens ?? m.OutputTokens ?? 0,
      totalTokens: m.totalTokens ?? m.TotalTokens ?? 0,
      model: m.model ?? m.Model ?? null,
      createdAt: m.createdAt ?? m.CreatedAt ?? '',
    }));
  }
}

export interface FiscalDevice {
  id: number;
  companyId: string;
  vkn: string | null;
  authorizedPerson: string | null;
  phone: string | null;
  /** Süre bitimi bildirimlerinin gönderileceği müşteri e-postası. */
  email: string | null;
  /** Cihazla verilen mobil hattın numarası (opsiyonel). */
  lineNumber: string | null;
  /** Hat kimin adına kayıtlı (Aquasoft ya da başka bir firma). */
  lineOwner: string | null;
  /** Turkcell / Vodafone / Türk Telekom / Diğer. */
  lineOperator: string | null;
  accountName: string | null;
  city: string | null;
  /** Cihazın verildiği şubenin adı (listede gösterilen değer). */
  branch: string | null;
  /** Seçilen şubenin Firebase kimliği; eski kayıtlarda boş. */
  branchId: string | null;
  /**
   * Şube seçimi eklenmeden önce elle yazılmış şube metni. Yalnızca şubesi
   * atanmamış kayıtlarda dolu gelir; gerçek şubeler girilirken referans olsun
   * diye geçici bir sütunda gösteriliyor.
   */
  legacyBranch: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  usageArea: string | null;
  gmuFeeCollected: boolean;
  activationDate: string | null;
  expiryDate: string | null;
  invoiceStatus: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FiscalDeviceCreateDto {
  companyId: string;
  vkn?: string;
  authorizedPerson?: string;
  phone?: string;
  email?: string;
  lineNumber?: string;
  lineOwner?: string;
  lineOperator?: string;
  accountName?: string;
  city?: string;
  branch?: string;
  branchId?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  usageArea?: string;
  gmuFeeCollected: boolean;
  activationDate?: string | null;
  expiryDate?: string | null;
  invoiceStatus?: string;
  notes?: string;
}

export type FiscalDeviceUpdateDto = FiscalDeviceCreateDto;

/** Sayfalı yazarkasa listesi satırı — backend şirket adını join ile döner. */
export type FiscalDeviceWithCompany = FiscalDevice & {
  companyName?: string | null;
  /** Cihazı veren bayi; null = merkezin verdiği cihaz. Sunucuda damgalanır. */
  dealerId?: number | null;
  dealerName?: string | null;
};

/** Hatırlatmanın kaynağı — süreye bağlı her takip kalemi bu türlerden biridir. */
export type SuperAdminReminderKind =
  | 'FISCAL_DEVICE'
  | 'CONTRACT'
  | 'SUBSCRIPTION'
  | 'PRODUCT'
  // Şirket başına birleşik tahsilat kartı (sözleşme/abonelik + ürünler).
  | 'BILLING'
  // Demosu yaklaşan/dolmuş, henüz açık tahsilatı olmayan şirket (lisansa geçiş için).
  | 'DEMO';

/** Ertelenmiş / bu dönem için kapatılmış uyarı. */
export interface MutedReminder {
  id: number;
  key: string;
  kind: SuperAdminReminderKind;
  companyId: string;
  title: string;
  subtitle: string | null;
  expiryDate: string | null;
  daysRemaining: number;
  /** Doluysa bu tarihe kadar ertelendi; null = bu dönem için kapatıldı. */
  snoozedUntil: string | null;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Takibi bırakılmış şirket. */
export interface MutedCompany {
  companyId: string;
  name: string;
  mutedAt: string | null;
  mutedBy: string | null;
  reason: string | null;
}

export interface MutedRemindersResult {
  dismissed: MutedReminder[];
  companies: MutedCompany[];
}

/**
 * Süperadmin hatırlatma satırı (yazarkasa GMÜ süresi veya şirket sözleşmesi).
 * daysRemaining negatifse süre dolmuş, 0 bugün doluyor demektir.
 */
export interface SuperAdminReminder {
  kind: SuperAdminReminderKind;
  /** Liste anahtarı ve kapatma damgası için benzersiz kimlik. */
  key: string;
  companyId: string;
  title: string;
  subtitle: string | null;
  detail: string | null;
  expiryDate: string | null;
  daysRemaining: number;
  searchText: string | null;
  /**
   * Derin bağlantının açacağı sekme; sunucu çağıranın kapsamına göre seçer.
   * 'contracts' | 'fiscal-devices' | 'dealer-fiscal-devices'. Süper adminde bayi
   * cihazı ayrı sekmede listelendiği için o kalemler 'dealer-fiscal-devices'
   * hedefler; bayinin panelinde tek yazarkasa listesi vardır.
   */
  targetTab: string;
}

/**
 * Şube seçim/filtre seçeneği. Franchise alanları yazarkasa formunun şube
 * seçilince firma bilgilerini doldurabilmesi için gelir; franchise olmayan
 * şubelerde boştur.
 */
export interface BranchOption {
  branchId: string;
  name: string;
  isActive: boolean;
  city?: string | null;
  phone?: string | null;
  isFranchise?: boolean;
  firmName?: string | null;
  firmTaxNumber?: string | null;
  firmTaxOffice?: string | null;
  firmPhone?: string | null;
  email?: string | null;
  firmEmail?: string | null;
}

/** Süperadmin entegrasyon genel bakış satırı (detay paneli için credential'lar dahil). */
export interface PlatformIntegrationRow {
  id: number;
  companyId: string;
  companyName: string;
  branchId: string | null;
  /** Şube adı — backend tenant DB'lerinden çözer; çözülemezse null. */
  branchName: string | null;
  platform: string;
  category: string;
  isEnabled: boolean;
  storeName: string | null;
  restaurantId: string | null;
  sellerId: string | null;
  apiKey: string | null;
  apiSecret: string | null;
  /** Yemeksepeti'nde chain code olarak kullanılır. */
  appSecretKey: string | null;
  restaurantSecretKey: string | null;
  isConnected: boolean;
  lastSyncAt: string | null;
  errorCount: number;
  lastErrorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export default SuperAdminService;
